import { ACCOUNT_SETTINGS_SECTIONS, type AccountSettingsSection } from "@loot-os/api-contract";
import { useSearch } from "@tanstack/react-router";

import { DEPOSIT_PARAM } from "../../../../lib/deposit.js";

/**
 * The Account page's tabs (the inventory is the Vault tab, its own route). Adding funds is a modal
 * over any page (`lib/deposit.ts`), not a tab; the old `?tab=wallet` opens it over Settings.
 */
export const ACCOUNT_TABS = ["settings", "history"] as const;
export type AccountTab = (typeof ACCOUNT_TABS)[number];

/**
 * `/account`'s search: the tab and settings panel showing. Stripe's checkout return
 * (`?purchase=…&session_id=…`) also lands here, and Add funds reads it from the URL itself.
 */
export interface AccountSearch {
  tab?: AccountTab;
  section?: AccountSettingsSection;
  /** Opens Add funds over the page (`lib/deposit.ts`); set here only for the old Wallet tab. */
  deposit?: 1;
}

const oneOf = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  typeof value === "string" && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

/**
 * The route's `validateSearch`: unknown tabs and panels fall back to the defaults. The router
 * drops a key this returns as undefined and keeps the ones it doesn't name (`?deposit=1`, Stripe's
 * `?purchase=…`), so the old Wallet tab (`?tab=wallet`) becomes `?deposit=1`: Add funds over
 * Settings.
 */
export function parseAccountSearch(search: Record<string, unknown>): AccountSearch {
  return {
    tab: oneOf(ACCOUNT_TABS, search.tab),
    section: oneOf(ACCOUNT_SETTINGS_SECTIONS, search.section),
    ...(search.tab === "wallet" ? { [DEPOSIT_PARAM]: 1 as const } : {}),
  };
}

/** The account page at `tab` (and, under Settings, `section`), as a link. */
export function accountHref(tab: AccountTab, section?: AccountSettingsSection): string {
  return section ? `/account?tab=${tab}&section=${section}` : `/account?tab=${tab}`;
}

/**
 * The tab and panel showing. Settings is the default: the account menu's Account means the
 * shopper's own settings, and the balance has its own place (the header, and Add funds). A panel
 * the store doesn't offer (Verification, with verification off) falls back to Profile.
 */
export function useAccountView(kycRequired: boolean): {
  tab: AccountTab;
  section: AccountSettingsSection;
} {
  const search = useSearch({ from: "/account" });
  const tab = search.tab ?? "settings";
  const wanted = search.section ?? "profile";
  const section = wanted === "verification" && !kycRequired ? "profile" : wanted;
  return { tab, section };
}
